import { JSX } from 'react';
import { TipTapContent } from '@openapi/model/tipTapContent';
import { TipTapContentContentItem } from '@openapi/model/tipTapContentContentItem';
import { TipTapParagraph } from '@openapi/model/tipTapParagraph';
import { TipTapParagraphContentItem } from '@openapi/model/tipTapParagraphContentItem';
import { TipTapText } from '@openapi/model/tipTapText';
import { TipTapMark } from '@openapi/model/tipTapMark';
import { TipTapImage } from '@openapi/model/tipTapImage';
import { TipTapHeading } from '@openapi/model/tipTapHeading';
import { TipTapBulletList } from '@openapi/model/tipTapBulletList';
import { TipTapListItem } from '@openapi/model/tipTapListItem';
import { TipTapBlockquote } from '@openapi/model/tipTapBlockquote';
import { TipTapOrderedList } from '@openapi/model/tipTapOrderedList';
import { TipTapHorizontalRule } from '@openapi/model/tipTapHorizontalRule';
import { TipTapHardBreak } from '@openapi/model/tipTapHardBreak';

function renderTipTapNode(
    node:
        | TipTapContentContentItem
        | TipTapParagraphContentItem
        | TipTapHeading
        | TipTapBulletList
        | TipTapOrderedList
        | TipTapListItem
        | TipTapBlockquote
        | TipTapText
        | TipTapImage
        | TipTapHorizontalRule
        | TipTapHardBreak,
    key?: number,
): JSX.Element | string | null {
    if (!node) return null;
    switch (node.type) {
        case 'paragraph':
            return (
                <p key={key}>
                    {(node as TipTapParagraph).content?.map((child, idx) =>
                        renderTipTapNode(child, idx),
                    )}
                </p>
            );
        case 'heading': {
            const level = (node as TipTapHeading).attrs?.level || 1;
            const Tag = `h${level}` as keyof JSX.IntrinsicElements;
            return (
                <Tag key={key}>
                    {(node as TipTapHeading).content?.map((child, idx) =>
                        renderTipTapNode(child, idx),
                    )}
                </Tag>
            );
        }
        case 'bulletList':
            return (
                <ul key={key}>
                    {(node as TipTapBulletList).content?.map((child, idx) =>
                        renderTipTapNode(child, idx),
                    )}
                </ul>
            );
        case 'orderedList':
            return (
                <ol key={key} start={(node as TipTapOrderedList).attrs?.order || 1}>
                    {(node as TipTapOrderedList).content?.map((child, idx) =>
                        renderTipTapNode(child, idx),
                    )}
                </ol>
            );
        case 'listItem':
            return (
                <li key={key}>
                    {(node as TipTapListItem).content?.map((child, idx) =>
                        renderTipTapNode(child, idx),
                    )}
                </li>
            );
        case 'blockquote':
            return (
                <blockquote key={key}>
                    {(node as TipTapBlockquote).content?.map((child, idx) =>
                        renderTipTapNode(child, idx),
                    )}
                </blockquote>
            );
        case 'horizontalRule':
            return <hr key={key} />;
        case 'text': {
            let el: JSX.Element | string = (node as TipTapText).text;
            if ((node as TipTapText).marks) {
                (node as TipTapText).marks!.forEach((mark: TipTapMark) => {
                    if (mark.type === 'bold') {
                        el = <strong>{el}</strong>;
                    }
                    if (mark.type === 'italic') {
                        el = <em>{el}</em>;
                    }
                    if (mark.type === 'link') {
                        el = (
                            <a
                                href={mark.attrs?.href}
                                target={mark.attrs?.target}
                                rel={mark.attrs?.rel}
                                className={mark.attrs?.class || undefined}
                            >
                                {el}
                            </a>
                        );
                    }
                });
            }
            return el;
        }
        case 'hardBreak':
            return <br key={key} />;
        case 'image':
            return (
                <img
                    key={key}
                    src={(node as TipTapImage).attrs?.src}
                    alt={(node as TipTapImage).attrs?.alt || ''}
                    title={(node as TipTapImage).attrs?.title || ''}
                    style={{ maxWidth: '100%', display: 'block', margin: '1em 0' }}
                />
            );
        default:
            return null;
    }
}

export function TipTapRenderer({ content }: { content: TipTapContent }) {
    if (!content?.content) return null;

    return <div>{content.content.map((node, idx) => renderTipTapNode(node, idx))}</div>;
}

export default TipTapRenderer;
